import {
  AccountBalance,
  AccountBalanceQuery,
  AccountId,
  Client,
  ContractExecuteTransaction,
  ContractFunctionParameters,
  Hbar,
  Timestamp,
  TransactionId,
  TransactionReceipt,
} from "@hashgraph/sdk";
import { BladeService } from "services/blade.service";
import { config } from "config/config";
import { HashConnectTypes, MessageTypes } from "hashconnect";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { tvlUpdateInterval } from "constants/constants";
import { StorageService } from "services/storage.service";
import { HashConnectService } from "services/hash-connect.service";
import { TransactionCResponse } from "types/TransactionCResponse";
import { WalletStatus } from "components/ConnectWalletDropdown/ConnectWalletDropdown";
import { action } from "reducer";
import { initialState, INITIAL_SAVE_DATA, reducer } from "reducer/reducer";

import { WALLET_CONNECT_SUCCESS } from "constants/analytics";
import {
  FirestoreData,
  MaintainanceData,
  Networks,
  SaveData,
  TransactionType,
  WalletExtensionType,
  WithdrawStatus,
} from "types/common";
import getFireStoreData from "services/firestore";
import { getMaintainanceData, sentryErrorLog, trackEvent } from "utils";

interface PropsType {
  children: React.ReactNode;
  hashConnect: HashConnectService;
  network: Networks;
  metadata?: HashConnectTypes.AppMetadata;
  debug?: boolean;
}

const transactionCResponse = {
  status: "",
  transactionId: "",
};

interface HashConnectProviderAPI {
  connect: (type: ConnectType) => void;
  disconnect: () => void;
  accountBalance: AccountBalance | null;
  selectedAccount: string;
  walletData: SaveData;
  network: Networks;
  metadata?: HashConnectTypes.AppMetadata;
  installedExtensions: HashConnectTypes.WalletMetadata | null;
  isBladeExtensionInstalled: boolean;
  isExtensionInstalled: boolean;
  withdrawStatus: WithdrawStatus;
  transactionType: TransactionType;
  transactionId: string | null;
  setTransactionId: (transactionId: string | null) => void;
  setWithdrawStatus: (status: WithdrawStatus) => void;
  status: string;
  stake: (amount: number) => Promise<TransactionCResponse>;
  unstake: (
    amount: number,
    exchangeRate: number
  ) => Promise<TransactionCResponse>;
  withdraw: (
    index: number,
    contractType: string | null
  ) => Promise<TransactionCResponse>;
  withdrawId: number | null;
  setWithdrawId: (withdrawId: number | null) => void;
  contractType: string | null;
  setContractType: (contractType: string | null) => void;
  transactionStatus: string;
  setTransActionStatus: (status: string) => void;
  signTransaction: (transaction: string) => Promise<string | null>;
  walletModal: boolean;
  showWalletModal: (modalDisplay: boolean) => void;
  tvl: number;
  connectedAccountType: string;
  firestoreData: FirestoreData;
  defiData: any;
  maintainanceData: MaintainanceData;
  setMaintainanceData: (maintainanceData: MaintainanceData) => void;
}

export enum ConnectType {
  CHROME_EXTENSION,
  BLADE_WALLET,
  INSTALL_EXTENSION,
}

const bladeService = new BladeService();

let APP_CONFIG: HashConnectTypes.AppMetadata = {
  name: "Stader HBAR staking",
  description:
    "Liquid staking with Stader. Stake HBAR with Stader to earn rewards while keeping full control of your staked tokens. Start earning rewards in just a few clicks",
  icon: "https://hedera.staderlabs.com/static/stader_logo.svg",
};

export const HashConnectAPIContext =
  React.createContext<HashConnectProviderAPI>({
    connect: () => null,
    disconnect: () => null,
    accountBalance: null,
    selectedAccount: "",
    isBladeExtensionInstalled: false,
    isExtensionInstalled: false,
    walletData: INITIAL_SAVE_DATA,
    network: config.network.name as Networks,
    installedExtensions: null,
    transactionType: "stake",
    withdrawStatus: "NONE",
    withdrawId: null,
    setWithdrawId: () => {},
    contractType: null,
    setContractType: () => {},
    transactionId: null,
    setTransactionId: () => {},
    status: "INITIALIZING",
    setWithdrawStatus: () => {},
    stake: () => Promise.resolve(transactionCResponse as TransactionCResponse),
    unstake: () =>
      Promise.resolve(transactionCResponse as TransactionCResponse),
    withdraw: () =>
      Promise.resolve(transactionCResponse as TransactionCResponse),
    transactionStatus: "",
    setTransActionStatus: () => null,
    signTransaction: () => Promise.resolve(null),
    walletModal: false,
    showWalletModal: () => null,
    tvl: 0,
    connectedAccountType: "",
    firestoreData: {} as FirestoreData,
    defiData: {},
    maintainanceData: {} as MaintainanceData,
    setMaintainanceData: () => {},
  });

let tvlInterval: any;

export default function HashConnectProvider({
  children,
  hashConnect,
  metadata: metadata,
  network: network,
  debug,
}: PropsType) {
  debug = true;

  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    saveData,
    installedExtensions,
    isExtensionInstalled,
    accountBalance,
    tvl,
    selectedAccount,
    transactionType,
    status,
    transactionStatus,
    withdrawStatus,
    withdrawId,
    contractType,
    transactionId,
    connectedAccountType,
    isBladeExtensionInstalled,
    walletModal,
    firestoreData,
    defiData,
    maintainanceData,
  } = state;

  const {
    setStatus,
    setSaveData,
    setBladeExtensionInstalled,
    setInstalledExtensions,
    setIsExtensionInstalled,
    setConnectedAccountType,
    setSelectedAccount,
    setAccountBalance,
    setTVL,
    setTransactionId,
    setTransactionType,
    setWithdrawId,
    setContractType,
    setWithdrawStatus,
    showWalletModal,
    setTransactionStatus,
    setFireStoreData,
    setDefiData,
    setMaintainanceData,
  } = action;

  useEffect(() => {
    (async function getData() {
      const { firestoreData, defiData } = await getFireStoreData(
        config.firebaseConfigDocument,
        config.firebaseDefiDocument
      );
      dispatch(setFireStoreData(firestoreData));
      dispatch(setDefiData(defiData));
    })();
  }, []);

  const [
    ,
    // networkError,
    setNetworkError,
  ] = useState<boolean>(false);

  const statusRef = useRef(status);

  const setStatusData = (status: string) => {
    statusRef.current = status;
    dispatch(setStatus(status));
  };

  const saveDataRef = useRef(saveData);

  const setSaveInfo = (saveData: SaveData) => {
    saveDataRef.current = saveData;
    dispatch(setSaveData(saveData));
  };

  useEffect(() => {
    (async () => {
      if (firestoreData) {
        const data = await getMaintainanceData();
        dispatch(setMaintainanceData(data));
      }
    })();
  }, [firestoreData]);

  //? Initialize the package in mount
  const initializeHashConnect = async () => {
    const saveData = INITIAL_SAVE_DATA;
    const localData = StorageService.loadLocalData();
    await bladeService.checkExtensionInstalled();
    dispatch(setBladeExtensionInstalled(bladeService.isExtensionInstalled));
    bladeService.setSigner();
    // console.log("localData", localData);
    try {
      if (!localData) {
        //first init and store the private for later
        let initData = await hashConnect.initialize(
          metadata ?? APP_CONFIG,
          network,
          debug
        );
        saveData.privateKey = initData.privateKey;

        saveData.topic = initData.topic;
        // console.log({ state });
        //generate a pairing string, which you can display and generate a QR code from
        saveData.pairingString = initData.pairingString;
      } else {
        // if (debug) console.log("====Local data found====", localData);
        //use loaded data for initialization + connection
        if (localData.walletExtensionType == "hashpack") {
          await hashConnect.init(metadata ?? APP_CONFIG, localData?.privateKey);
          dispatch(setInstalledExtensions(localData?.pairedWalletData));
          dispatch(setIsExtensionInstalled(true));
          await hashConnect.connect(
            localData?.topic,
            localData?.pairedWalletData ?? metadata
          );
        } else {
          await bladeService.loadWallet();
        }
      }
    } catch (error) {
      setNetworkError(true);
    } finally {
      if (localData) {
        setSaveInfo({ ...saveData, ...localData });
        dispatch(setConnectedAccountType(localData.walletExtensionType));
        localData.accountIds &&
          dispatch(setSelectedAccount(localData.accountIds[0]));
        localData.accountIds && (await getAccounts(localData.accountIds[0]));
      } else {
        setSaveInfo({ ...saveData, ...saveData });
        // await getAccounts(saveData.accountIds[0]);
      }
      // await getAccounts(saveData.accountIds[0]);
      // console.log(saveData);

      if (status === WalletStatus.INITIALIZING) {
        setStatusData(WalletStatus.WALLET_NOT_CONNECTED);
      }
    }
    return localData;
  };

  const saveDataInLocalStorage = async (
    data: MessageTypes.ApprovePairing,
    extensionType?: WalletExtensionType
  ) => {
    const { metadata, ...restData } = data;
    const saveObj: SaveData = {
      ...saveData,
      pairedWalletData: metadata,
      pairedAccounts: restData.accountIds,
      walletExtensionType: extensionType || connectedAccountType,
      ...restData,
    };
    // console.log(saveObj, "saveObj");
    // await setSaveData((prevSaveData) => {
    //   prevSaveData.pairedWalletData = metadata;
    //   console.log("restData", { ...prevSaveData, ...restData });
    //   return { ...prevSaveData, ...restData };
    // });
    // console.log("saveData", saveData);
    setSaveInfo(saveObj);
    // setAccountId(restData.accountIds[0]);
    StorageService.saveData(saveObj);
    if (
      saveObj !== undefined &&
      saveObj.accountIds &&
      saveObj.accountIds[0] !== undefined
    ) {
      trackEvent(WALLET_CONNECT_SUCCESS, { address: saveObj.accountIds[0] });
      dispatch(setSelectedAccount(saveObj.accountIds[0]));
      await getAccounts(saveObj.accountIds[0]!);
    }
  };

  // const additionalAccountResponseEventHandler = (
  //   data: MessageTypes.AdditionalAccountResponse
  // ) => {
  //   if (debug) console.debug("additionalAccountResponseEvent", data);
  //   // Do a thing
  // };

  const foundExtensionEventHandler = (
    data: HashConnectTypes.WalletMetadata
  ) => {
    // Do a thing
    if (status === WalletStatus.INITIALIZING) {
      setStatusData(WalletStatus.WALLET_NOT_CONNECTED);
      dispatch(setInstalledExtensions(data as HashConnectTypes.WalletMetadata));
    }
  };

  const pairingEventHandler = (data: MessageTypes.ApprovePairing) => {
    // Save Data to localStorage

    saveDataInLocalStorage(data, "hashpack");
  };

  const transactionHandler = () => {
    // console.log("received data", data);
  };

  // useEffect(() => {
  //   const listener = (event: MessageEvent) => {
  //     if (event.data.type === "hashconnect-query-extension-response") {
  //       setExtensionInstalled(true);
  //     }
  //   };
  //   window.addEventListener("message", listener);
  //   window.postMessage({ type: "hashconnect-query-extension" }, "*");
  //   setTimeout(() => {
  //     window.removeEventListener("message", listener);
  //     if (!isExtensionInstalled) {
  //       setStatus(WalletStatus.WALLET_NOT_CONNECTED);
  //     }
  //   }, 5000);
  //   return () => {
  //     window.removeEventListener("message", listener);
  //   };
  // });

  useEffect(() => {
    //Intialize the setup

    hashConnect.listeners.foundExtensionEvent.once(foundExtensionEventHandler);
    hashConnect.listeners.pairingEvent.on(pairingEventHandler);
    hashConnect.listeners.transactionEvent.on(transactionHandler);

    hashConnect.listeners.transactionResolver = () => {};
    //

    getTvl();
    tvlInterval = setInterval(async () => {
      await getTvl();
    }, tvlUpdateInterval);
    initializeHashConnect().then(() => {
      if (StorageService.loadLocalData()) return;
      //   setTimeout(() => {
      //     if (!installedExtensions || !bladeService.isExtensionInstalled) {

      //       setStatus(WalletStatus.WALLET_NOT_CONNECTED);
      //     }
      //   }, 5000);
    });

    // Attach event handlers

    return () => {
      // Detach existing handlers

      clearInterval(tvlInterval);
      hashConnect.listeners.foundExtensionEvent.off(foundExtensionEventHandler);
      hashConnect.listeners.pairingEvent.off(pairingEventHandler);
      hashConnect.listeners.transactionEvent.off(transactionHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedAccountType]);

  // useEffect(() => {
  //   console.log("status", status);
  // }, [status]);

  const connect = async (type: ConnectType) => {
    // console.log({ type, installedExtensions });
    // switch (type) {
    //   case ConnectType.CHROME_EXTENSION:
    //     await hashConnect.connectToLocalWallet(saveData?.pairingString);
    //     break;
    //   case ConnectType.INSTALL_EXTENSION:
    //     window.open(config.extension_url, "_blank");
    //     break;
    // }
    if (type === ConnectType.BLADE_WALLET && !isBladeExtensionInstalled) {
      window.open(config.blade_extension_url, "_blank");
    } else if (type === ConnectType.BLADE_WALLET) {
      const account = await bladeService.loadWallet();
      setStatusData(WalletStatus.WALLET_CONNECTED);
      dispatch(setConnectedAccountType("blade"));
      dispatch(setSelectedAccount(account.toString()));
      const balance = await bladeService.getBalance();
      dispatch(setAccountBalance(balance));
      const walletData = bladeService.getWalletData();
      saveDataInLocalStorage(walletData, "blade");
    } else if (installedExtensions) {
      dispatch(setConnectedAccountType("hashpack"));
      hashConnect.connectToLocalWallet(saveData?.pairingString);
    } else {
      window.open(config.extension_url, "_blank");
    }
  };

  const disconnect = () => {
    // setSaveData(INITIAL_SAVE_DATA);
    setStatusData(WalletStatus.WALLET_NOT_CONNECTED);
    dispatch(setSelectedAccount(""));
    dispatch(setAccountBalance(null));
    // setInstalledExtensions(null);

    StorageService.remove();
    initializeHashConnect();
  };

  const getTvl = async () => {
    try {
      const data: any = await fetch(config.tvlURL);
      const { value } = await data.json();
      dispatch(setTVL(value));
    } catch {
      setNetworkError(true);
    }
  };

  const getAccounts = async (accountId: string) => {
    //Create the account info query
    try {
      if (connectedAccountType === "hashpack") {
        const query = new AccountBalanceQuery().setAccountId(accountId);
        const client = Client.forName(config.network.name);
        // Sign with client operator private key and submit the query to a Hedera network
        const balance = await query.execute(client);
        dispatch(setAccountBalance(balance));
      } else if (connectedAccountType === "blade") {
        const balance = await bladeService.getBalance();
        dispatch(setAccountBalance(balance));
      }
      if (connectedAccountType !== "") {
        setStatusData("WALLET_CONNECTED");
      }
    } catch (error: any) {
      setNetworkError(true);
    }
  };

  const getTimeStamp = async () => {
    let timestamp;
    try {
      //TODO:  url does not work for vercel temp deployment so added full link. will remove this later
      const resp: any = await fetch(
        `https://hedera.staderlabs.com/api/timestamp`
      );
      const response: any = await resp.json();
      if (response?.timestamp) {
        timestamp = response.timestamp;
      }
    } catch (e) {
      timestamp = Math.floor(new Date().getTime() / 1000);
      sentryErrorLog(e);
    }
    return timestamp;
  };

  const signTransaction = async (transactionString: string) => {
    // console.log("transactionString", transactionString);
    const transaction = Buffer.from(transactionString, "base64");

    // console.log("transaction", transaction.buffer);

    const response: MessageTypes.TransactionResponse = await sendTransaction(
      transaction,
      selectedAccount,
      true
    );

    // console.log("response", response);
    if (response.success && response.signedTransaction) {
      // console.log("signedTransaction", signedTransaction);

      const signedTransaction = Buffer.from(
        response.signedTransaction
      ).toString("base64");
      // console.log(encodedSignature);
      // const output: signedTransactionParams = {
      //   userId: selectedAccount,
      //   signature: encodedSignature,
      // };
      // console.log("output", output);
      return signedTransaction;
    }

    return null;
  };

  const getTransactionURL = (transaction: ContractExecuteTransaction) => {
    let transactionIdURL =
      transaction.transactionId?.accountId +
      "-" +
      transaction.transactionId?.validStart;
    transactionIdURL = transactionIdURL.slice(0, -2) + "-000000000";
    return transactionIdURL;
  };

  const stakeHashpack = async (
    accountId: AccountId,
    transaction: ContractExecuteTransaction,
    isWithDrawn: boolean = false
  ) => {
    let transactionStatus = "";
    let transactionId = "";
    const transactionBytes = transaction.toBytes();

    try {
      transactionId = getTransactionURL(transaction);
      dispatch(setTransactionId(transactionId));
      const response: MessageTypes.TransactionResponse = await sendTransaction(
        transactionBytes,
        accountId.toString(),
        false
      );

      if (response.success) {
        transactionStatus = "SUCCESS";
        if (!isWithDrawn) {
          dispatch(setTransactionStatus("SUCCESS"));
        } else {
          dispatch(setWithdrawStatus("SUCCESS"));
        }
      }

      if (response.success && !response.signedTransaction) {
        // console.log(TransactionReceipt.fromBytes(response.receipt as Uint8Array));
      } else if (response.success && response.signedTransaction) {
        // console.log(
        //   Transaction.fromBytes(response.signedTransaction as Uint8Array)
        // );
      } else {
        transactionStatus = "FAILED";
        if (!isWithDrawn) {
          dispatch(setTransactionStatus("FAILED"));
        } else {
          dispatch(setWithdrawStatus("FAILED"));
        }
      }
    } catch (e) {
      dispatch(setTransactionId(null));
      transactionStatus = "FAILED";
      if (!isWithDrawn) {
        dispatch(setTransactionStatus("FAILED"));
      } else {
        dispatch(setWithdrawStatus("FAILED"));
      }
    } finally {
      dispatch(setTransactionId(null));
      dispatch(setWithdrawId(null));
      dispatch(setContractType(null));
      dispatch(setTransactionStatus(""));
    }
    // showTransactionToast(transactionStatus);
    getAccounts(accountId.toString());
    getTvl();

    return { status: transactionStatus, transactionId };
  };

  const stakeBlade = async (
    accountId: AccountId,
    transaction: ContractExecuteTransaction,
    isWithDrawn: boolean = false
  ) => {
    let transactionStatus = "";
    let transactionId = "";
    try {
      const response = await bladeService.sendTransaction(transaction);
      transactionId = getTransactionURL(transaction);
      dispatch(setTransactionId(transactionId));

      const client = Client.forName(config.network.name);
      const receipt: TransactionReceipt = await response.getReceipt(client);
      if (receipt.status.toString() === "SUCCESS") {
        transactionStatus = "SUCCESS";
        if (!isWithDrawn) {
          dispatch(setTransactionStatus("SUCCESS"));
        } else {
          dispatch(setWithdrawStatus("SUCCESS"));
        }
      } else {
        transactionStatus = "FAILED";
        if (!isWithDrawn) {
          dispatch(setTransactionStatus("FAILED"));
        } else {
          dispatch(setWithdrawStatus("FAILED"));
        }
      }
    } catch (error) {
      transactionStatus = "FAILED";
      if (!isWithDrawn) {
        dispatch(setTransactionStatus("FAILED"));
      } else {
        dispatch(setWithdrawStatus("FAILED"));
      }
    } finally {
      dispatch(setTransactionId(null));
      dispatch(setWithdrawId(null));
      dispatch(setContractType(null));
    }
    getAccounts(accountId.toString());
    getTvl();
    return { status: transactionStatus, transactionId };
  };

  const stake = async (amount: number) => {
    const timestamp = await getTimeStamp();
    const maintainanceData = await getMaintainanceData();
    dispatch(setMaintainanceData(maintainanceData));
    if (maintainanceData?.isActive) {
      return { status: "UNDER_MAINTAINANCE", transactionId: "" };
    }
    dispatch(setTransactionStatus("START"));
    dispatch(setTransactionType("stake"));
    // const timestamp = Math. floor(new Date().getTime() / 1000)
    const validStart = new Timestamp(timestamp, 0);

    const accountId: AccountId = AccountId.fromString(
      selectedAccount as string
    );

    let transId = TransactionId.withValidStart(accountId, validStart);

    const transaction = new ContractExecuteTransaction()
      .setContractId(config.ids.stakingContractId)
      .setGas(2_000_000)
      .setPayableAmount(amount)
      .setFunction("stake")
      .setNodeAccountIds([
        new AccountId(4),
        new AccountId(3),
        new AccountId(5),
        new AccountId(7),
        new AccountId(8),
        new AccountId(9),
        new AccountId(10),
        new AccountId(11),
      ])
      .setTransactionId(transId)
      .freeze();

    if (connectedAccountType === "hashpack") {
      return stakeHashpack(accountId, transaction);
    } else if (connectedAccountType === "blade") {
      return stakeBlade(accountId, transaction);
    }
    return { status: "INVALID_WALLET", transactionId: "" };
  };

  const tinyBarValue = (amountVal: number) => {
    return new Hbar(amountVal.toString()).toTinybars().toNumber();
  };

  const unstake = async (amount: number, exchangeRate: number) => {
    const maintainanceData = await getMaintainanceData();
    dispatch(setMaintainanceData(maintainanceData));
    if (maintainanceData?.isActive) {
      return { status: "UNDER_MAINTAINANCE", transactionId: "" };
    }
    dispatch(setTransactionStatus("START"));
    dispatch(setTransactionType("unstake"));
    const accountId: AccountId = AccountId.fromString(
      selectedAccount as string
    );

    let transId = TransactionId.generate(accountId);
    const unstakeAmount = tinyBarValue(amount);
    const transaction = new ContractExecuteTransaction()
      .setContractId(config.ids.stakingContractId)
      .setGas(2_000_000)
      .setTransactionMemo(exchangeRate.toString())
      .setFunction(
        "unStake",
        new ContractFunctionParameters().addUint256(unstakeAmount)
      )
      .setNodeAccountIds([
        new AccountId(4),
        new AccountId(3),
        new AccountId(5),
        new AccountId(7),
        new AccountId(8),
        new AccountId(9),
        new AccountId(10),
        new AccountId(11),
      ])
      .setTransactionId(transId)
      .freeze();

    if (connectedAccountType === "hashpack") {
      return stakeHashpack(accountId, transaction);
    } else if (connectedAccountType === "blade") {
      return stakeBlade(accountId, transaction);
    }
    return { status: "INVALID_WALLET", transactionId: "" };
  };

  const withdraw = async (index: number, contractType: string | null) => {
    const maintainanceData = await getMaintainanceData();
    dispatch(setMaintainanceData(maintainanceData));
    if (maintainanceData?.isActive) {
      return { status: "UNDER_MAINTAINANCE", transactionId: "" };
    }
    dispatch(setWithdrawStatus("IN_PROGRESS"));
    dispatch(setWithdrawId(index));
    dispatch(setContractType(contractType));
    const accountId: AccountId = AccountId.fromString(
      selectedAccount as string
    );

    let transId = TransactionId.generate(accountId);
    const contractId =
      contractType === "old"
        ? config.ids.oldUndelegationContractId
        : config.ids.undelegationContractId;
    const transaction = new ContractExecuteTransaction()
      .setContractId(contractId)
      .setGas(2_000_000)
      .setFunction(
        "withdraw",
        new ContractFunctionParameters().addUint256(index)
      )
      .setNodeAccountIds([
        new AccountId(4),
        new AccountId(3),
        new AccountId(5),
        new AccountId(7),
        new AccountId(8),
        new AccountId(9),
        new AccountId(10),
        new AccountId(11),
      ])
      .setTransactionId(transId)
      .freeze();

    if (connectedAccountType === "hashpack") {
      return stakeHashpack(accountId, transaction, true);
    } else if (connectedAccountType === "blade") {
      return stakeBlade(accountId, transaction, true);
    }
    return { status: "INVALID_WALLET", transactionId: "" };
  };

  const sendTransaction = async (
    trans: Uint8Array,
    acctToSign: string,
    return_trans: boolean = false
  ) => {
    const topic = saveData.topic;
    const transaction: MessageTypes.Transaction = {
      topic: topic,
      byteArray: trans,

      metadata: {
        accountToSign: acctToSign,
        returnTransaction: return_trans,
      },
    };
    const response = await hashConnect.sendTransaction(topic, transaction);

    return response;
  };

  return (
    <HashConnectAPIContext.Provider
      value={{
        connect,
        disconnect,
        accountBalance,
        selectedAccount,
        walletData: saveData,
        network: network,
        installedExtensions,
        isBladeExtensionInstalled,
        isExtensionInstalled,
        transactionType,
        status,
        stake,
        unstake,
        transactionId,
        setTransactionId: async (transactionId) => {
          dispatch(setTransactionId(transactionId));
        },
        withdraw,
        withdrawId,
        setWithdrawId: async (withdrawId) => {
          dispatch(setWithdrawId(withdrawId));
        },
        contractType,
        setContractType: async (contractType) => {
          dispatch(setContractType(contractType));
        },
        setWithdrawStatus: async (withdrawStatus) => {
          dispatch(setWithdrawStatus(withdrawStatus));
        },
        withdrawStatus,
        transactionStatus,
        setTransActionStatus: async (newStatus) => {
          dispatch(setTransactionStatus(newStatus));
          if (
            isBladeExtensionInstalled &&
            connectedAccountType === "blade" &&
            newStatus == ""
          ) {
            const balance = await bladeService.getBalance();
            dispatch(setAccountBalance(balance));
          }
        },
        signTransaction,
        walletModal,
        showWalletModal: async (walletModal) => {
          dispatch(showWalletModal(walletModal));
        },
        tvl,
        connectedAccountType,
        firestoreData,
        defiData,
        maintainanceData,
        setMaintainanceData: async (maintainanceData: MaintainanceData) => {
          dispatch(setMaintainanceData(maintainanceData));
        },
      }}
    >
      {children}
    </HashConnectAPIContext.Provider>
  );
}

const defaultProps: Partial<PropsType> = {
  metadata: {
    name: "Stader HBAR staking",
    description:
      "Liquid staking with Stader. Stake HBAR with Stader to earn rewards while keeping full control of your staked tokens. Start earning rewards in just a few clicks",
    icon: "https://hedera.staderlabs.com/static/stader_logo.svg",
  },
  network: config.network.name as Networks,
  debug: false,
};

HashConnectProvider.defaultProps = defaultProps;
